import { useCallback } from 'react';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import AudioOutlined from '@ant-design/icons/AudioOutlined';

// project imports
import useSpeechToText from 'hooks/useSpeechToText';

// ==============================|| LISTENING INDICATOR — ANIMATED EQUALIZER BARS ||============================== //

function ListeningIndicator({ color }) {
  const bars = [0, 1, 2, 3];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        width: 18,
        height: 18
      }}
    >
      {bars.map((i) => (
        <Box
          key={i}
          sx={{
            width: 3,
            borderRadius: 1,
            bgcolor: color,
            animation: `speechEqualizer 1s ease-in-out ${i * 0.15}s infinite`,
            '@keyframes speechEqualizer': {
              '0%, 100%': { height: 4 },
              '25%': { height: i % 2 === 0 ? 14 : 10 },
              '50%': { height: i % 2 === 0 ? 8 : 16 },
              '75%': { height: i % 2 === 0 ? 12 : 6 }
            }
          }}
        />
      ))}
    </Box>
  );
}

// ==============================|| SPEECH TEXT FIELD ||============================== //

export default function SpeechTextField({ value, onChange, provider, sx, ...textFieldProps }) {
  const theme = useTheme();
  const intl = useIntl();

  const handleFinalResult = useCallback(
    (text) => {
      const currentValue = value || '';
      const separator = currentValue && !currentValue.endsWith(' ') ? ' ' : '';
      const updatedValue = currentValue + separator + text;
      onChange?.({ target: { value: updatedValue } });
    },
    [value, onChange]
  );

  const { isListening, isSupported, interimTranscript, error, startListening, stopListening } = useSpeechToText({
    provider,
    onFinalResult: handleFinalResult
  });

  const handleMicToggle = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const displayValue = isListening && interimTranscript ? (value || '') + (value ? ' ' : '') + interimTranscript : value;

  if (!isSupported) {
    return <TextField value={value} onChange={onChange} sx={sx} {...textFieldProps} />;
  }

  const micTooltip = isListening
    ? intl.formatMessage({ id: 'speech.stopListening' })
    : intl.formatMessage({ id: 'speech.startListening' });

  return (
    <TextField
      value={displayValue}
      onChange={onChange}
      error={!!error || textFieldProps.error}
      helperText={error || textFieldProps.helperText}
      {...textFieldProps}
      sx={{
        ...sx,
        '& .MuiOutlinedInput-root': {
          position: 'relative'
        },
        '& .MuiOutlinedInput-root .MuiInputAdornment-positionEnd': {
          position: 'absolute',
          right: 8,
          bottom: 8,
          margin: 0
        },
        '& .MuiOutlinedInput-root textarea': {
          paddingBottom: '28px'
        }
      }}
      slotProps={{
        ...textFieldProps.slotProps,
        input: {
          ...textFieldProps.slotProps?.input,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={micTooltip} arrow>
                <IconButton
                  onClick={handleMicToggle}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  size="small"
                  sx={{
                    color: isListening ? '#fff' : theme.palette.text.secondary,
                    bgcolor: isListening ? theme.palette.error.main : 'transparent',
                    borderRadius: isListening ? '50%' : '50%',
                    width: 32,
                    height: 32,
                    transition: 'all 0.3s ease',
                    boxShadow: isListening ? `0 0 0 4px ${alpha(theme.palette.error.main, 0.2)}` : 'none',
                    animation: isListening ? 'micPulseRing 2s ease-in-out infinite' : 'none',
                    '@keyframes micPulseRing': {
                      '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}` },
                      '50%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.error.main, 0)}` },
                      '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}` }
                    },
                    '&:hover': {
                      bgcolor: isListening ? theme.palette.error.dark : alpha(theme.palette.text.secondary, 0.1)
                    }
                  }}
                >
                  {isListening ? (
                    <ListeningIndicator color="#fff" />
                  ) : (
                    <AudioOutlined style={{ fontSize: '1rem' }} />
                  )}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }
      }}
    />
  );
}
